import React, { useState, useEffect } from "react";
import { Trash2, Send } from "lucide-react";
import { DeliveryMan } from "@apis/index";
import Typography from "@components/typography";
import Button from "@components/button";
import useConfig from "@hooks/useConfig";
import * as S from "../style";

interface CartProps {
  cart: any[];
  setCart: (cart: any[]) => void;
  deliveryMode: "pickup" | "delivery";
  setDeliveryMode: (mode: "pickup" | "delivery") => void;
  deliveryManId: number | null;
  setDeliveryManId: (id: number | null) => void;
  paymentStatus: "Paid" | "Unpaid";
  setPaymentStatus: (status: "Paid" | "Unpaid") => void;
  discountType: "flat" | "percentage";
  setDiscountType: (type: "flat" | "percentage") => void;
  discountValue: string;
  setDiscountValue: (value: string) => void;
  onCheckout: () => void;
  loading: boolean;
  canCheckout: boolean;
}

const Cart: React.FC<CartProps> = ({
  cart,
  setCart,
  deliveryMode,
  setDeliveryMode,
  deliveryManId,
  setDeliveryManId,
  paymentStatus,
  setPaymentStatus,
  discountType,
  setDiscountType,
  discountValue,
  setDiscountValue,
  onCheckout,
  loading,
  canCheckout,
}) => {
  const { bucketUrl } = useConfig();
  const [deliveryMen, setDeliveryMen] = useState<any[]>([]);

  useEffect(() => {
    const fetchDeliveryMen = async () => {
      try {
        const res = await DeliveryMan.list({ params: { limit: 100 } });
        setDeliveryMen(res?.items || []);
      } catch (err) {
        console.error("Failed to fetch delivery men");
      }
    };
    fetchDeliveryMen();
  }, []);

  const updateQuantity = (id: string | number, delta: number) => {
    setCart(
      cart.map((item) =>
        item.id === id
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item,
      ),
    );
  };

  const removeFromCart = (id: string | number) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const subtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );
  const numericDiscount = Math.max(0, Number(discountValue) || 0);
  const discountAmount =
    discountType === "percentage"
      ? Math.min(subtotal, (subtotal * Math.min(numericDiscount, 100)) / 100)
      : Math.min(subtotal, numericDiscount);
  const total = Math.max(0, subtotal - discountAmount);

  return (
    <S.Card>
      <Typography styleText="font-weight: 600; font-size: 18px; margin-bottom: 20px;">
        Current Order
      </Typography>

      <S.CartList>
        {cart.map((item) => (
          <S.CartItem key={item.id}>
            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flex: 1,
              }}
            >
              <img
                src={
                  item.images?.[0]?.image
                    ? `${bucketUrl}/${item.images[0].image}`
                    : "https://via.placeholder.com/150"
                }
                alt={item.name}
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "6px",
                  objectFit: "cover",
                }}
              />
              <div>
                <Typography styleText="font-weight: 500; font-size: 14px; line-height: 1.2; margin-bottom: 4px;">
                  {item.name}
                </Typography>
                <Typography styleText="font-size: 12px; color: #6B7280">
                  रू {item.price} × {item.quantity}
                </Typography>
              </div>
            </div>
            <S.QuantityControls>
              <button onClick={() => updateQuantity(item.id, -1)}>-</button>
              <span>{item.quantity}</span>
              <button onClick={() => updateQuantity(item.id, 1)}>+</button>
            </S.QuantityControls>
            <S.RemoveButton onClick={() => removeFromCart(item.id)}>
              <Trash2 size={16} color="#EF4444" />
            </S.RemoveButton>
          </S.CartItem>
        ))}
        {cart.length === 0 && (
          <S.EmptyCart>
            <Typography>Cart is empty</Typography>
          </S.EmptyCart>
        )}
      </S.CartList>

      <S.OptionSection>
        <Typography styleText="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: #374151;">
          Order Type
        </Typography>
        <S.DeliveryToggle>
          <S.ToggleOption
            active={deliveryMode === "pickup"}
            onClick={() => setDeliveryMode("pickup")}
          >
            In-Store Pickup
          </S.ToggleOption>
          {/* <S.ToggleOption
            active={deliveryMode === "delivery"}
            onClick={() => setDeliveryMode("delivery")}
          >
            Delivery
          </S.ToggleOption> */}
        </S.DeliveryToggle>
      </S.OptionSection>

      <S.OptionSection>
        <Typography styleText="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: #374151;">
          Payment Status
        </Typography>
        <S.DeliveryToggle>
          <S.ToggleOption
            active={paymentStatus === "Paid"}
            onClick={() => setPaymentStatus("Paid")}
          >
            Paid
          </S.ToggleOption>
          <S.ToggleOption
            active={paymentStatus === "Unpaid"}
            onClick={() => setPaymentStatus("Unpaid")}
          >
            Unpaid
          </S.ToggleOption>
        </S.DeliveryToggle>
      </S.OptionSection>

      {deliveryMode === "delivery" && (
        <S.OptionSection>
          <Typography styleText="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: #374151;">
            Assign Delivery Man
          </Typography>
          <select
            value={deliveryManId || ""}
            onChange={(e) => setDeliveryManId(Number(e.target.value))}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #D1D5DB",
              fontSize: "14px",
              backgroundColor: "white",
            }}
          >
            <option value="">-- Select Delivery Man --</option>
            {deliveryMen.map((man) => (
              <option key={man.id} value={man.id}>
                {man.name} ({man.phoneNo})
              </option>
            ))}
          </select>
        </S.OptionSection>
      )}

      <S.OptionSection>
        <Typography styleText="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: #374151;">
          Custom Discount
        </Typography>
        <S.DeliveryToggle>
          <S.ToggleOption
            active={discountType === "flat"}
            onClick={() => setDiscountType("flat")}
          >
            Flat
          </S.ToggleOption>
          <S.ToggleOption
            active={discountType === "percentage"}
            onClick={() => setDiscountType("percentage")}
          >
            Percent
          </S.ToggleOption>
        </S.DeliveryToggle>
        <input
          min="0"
          max={discountType === "percentage" ? 100 : undefined}
          type="number"
          value={discountValue}
          onChange={(e) => setDiscountValue(e.target.value)}
          placeholder={
            discountType === "percentage" ? "Enter %" : "Enter amount"
          }
          style={{
            width: "100%",
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #D1D5DB",
            fontSize: "14px",
            backgroundColor: "white",
          }}
        />
      </S.OptionSection>

      <S.TotalsArea>
        <S.TotalsRow>
          <Typography>Subtotal</Typography>
          <Typography styleText="font-weight: 600">रू {subtotal}</Typography>
        </S.TotalsRow>
        {discountAmount > 0 && (
          <S.TotalsRow>
            <Typography>Discount</Typography>
            <Typography styleText="font-weight: 600; color: #EF4444">
              - रू {discountAmount}
            </Typography>
          </S.TotalsRow>
        )}
        <S.Divider />
        <S.TotalsRow>
          <Typography styleText="font-weight: 600; font-size: 18px">
            Total
          </Typography>
          <Typography styleText="font-weight: 600; font-size: 18px; color: #10B981">
            रू {total}
          </Typography>
        </S.TotalsRow>
      </S.TotalsArea>

      <Button
        onClick={onCheckout}
        disabled={!canCheckout || loading}
        loading={loading}
        title={loading ? "Processing..." : "Place POS Order"}
        icon={<Send size={18} style={{ marginRight: "8px" }} />}
        styleBtn="width: 100%; justify-content: center;"
      />
    </S.Card>
  );
};

export default Cart;
